/**
 * constants/filterStyles.js
 *
 * ЦЕНТРАЛИЗОВАННОЕ ХРАНИЛИЩЕ КОНФИГОВ ВСЕХ ФИЛЬТРОВ
 *
 * АРХИТЕКТУРА (Filter Templates System):
 * ──────────────────────────────────────
 *
 * Новый подход (с Filter Templates):
 *   - Данные (цвета, labels) хранятся в src/constants/layerStyles.js
 *   - Фильтры ссылаются на стиль: { style: STYLE, styleKey: 'key' }
 *   - Items подтягиваются через getStyleFilterItems() резолвер
 *   - Результат: один источник истины, без дублирования
 *
 * Старый подход (fallback для совместимости):
 *   - items: массив значений прямо в конфиге
 *   - Поддерживается для переходного периода
 *
 * Структура фильтра:
 *   - id: уникальный идентификатор
 *   - title: название для отображения (defineMessage)
 *   - reduxPath: путь к полю в reducers/mapFilters.js
 *   - defaultValue: начальное значение в Redux state
 *   - style (опционально): ссылка на STYLE из layerStyles.js (новое)
 *   - styleKey (опционально): ключ для доступа к filterItems (новое)
 *   - items (опционально): массив значений (старое, fallback)
 *   - description (опционально): подробное описание
 *   - icon (опционально): тип иконки для отображения
 *   - type (опционально): тип фильтра (checkbox-items, radio-items, и т.д.)
 *
 * Миграция: Фаза 1 (Инфраструктура) - ТЕКУЩАЯ
 * Статус: 🔄 В ПРОЦЕССЕ
 */

import { defineMessage } from '@lingui/macro';
import * as V from './variables';
import { CLASSIFIED_PARKING_STYLE, POI_STYLE, RESTRICTIONS_STYLE, FUNCTIONAL_AREAS_STYLE, RESTRICTION_ZONES_STYLE, RESTRICTION_ZONES_V2_STYLE, SPEED_LIMITS_STYLE, DOWNTIME_SCOOTERS_STYLE, PARKING_ALERTS_STYLE, PARKING_1300_STYLE, TAPS_OPENS_DAYS_STYLE, TRACK_MODE_STYLE, TRACK_DAYS_STYLE, TRACK_PREFIX_STYLE, AGGREGATED_YEAR_STYLE, AGG_YEAR_AUGUST_STYLE, CLASS_DAY_PART_STYLE, PARKING_PERMISSION_STYLE, OA_ROTATION_DAYS_STYLE, PARKING_LOT_STATUS_STYLE, COMPETITORS_DAYS_STYLE, TAPS_OPENS_PERIODS_STYLE, START_FINISH_TRACK_DAYS_STYLE, TAPS_OPENS_MODE_STYLE, PARKINGS_ALERTS_PERIOD_STYLE, PARKINGS_ALERTS_DEVICE_STYLE, PARKINGS_ALERTS_ERROR_TYPES_STYLE, ALERTS_TYPES_STYLE, ALERTS_DEVICE_TYPES_STYLE, COMPETITORS_FILTER_STYLE, COMPETITORS_PREFIX_MODE_STYLE, OPERATION_AREAS_STATUS_STYLE, SB_WITH_ERRORS_STYLE, SERVICE_CENTERS_FILTER_STYLE, FUNCTIONAL_AREAS_YANDEX_URENT_STYLE, LISBOA_ZONES_STYLE, ANALYTIC_AREAS_STYLE, ISOCHRONES_WITH_SCOOTERS_STYLE, AGG_USER_TAPS_7D_STYLE, AGG_USER_TAPS_30D_STYLE, AGG_USER_OPENS_7D_STYLE, AGG_USER_OPENS_30D_STYLE, SCR_TRACKS_STYLE, SCR_INFRA_STYLE } from './layerStyles';

// ============================================================================
// Категория 1: PARKING - Фильтры парковок
// ============================================================================

/**
 * PARKING_FILTER - Статус парковки (основной)
 * Используется в: ParkingFilters.jsx
 * Redux path: mapFilters.parkingLotStatuses
 * Items из variables.js: parkingTypes
 */
export const PARKING_FILTER = {
    id: 'parking',
    title: defineMessage({ message: 'Статус парковки' }),
    description: defineMessage({
        message: 'Фильтрация парковок по статусу проверки и добавления',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: PARKING_LOT_STATUS_STYLE,
    styleKey: 'statuses',

    reduxPath: 'parkingLotStatuses',
    defaultValue: [ 'VERIFIED' ],
    icon: 'circle',
};

/**
 * CLASSIFIED_PARKING_FILTER - Классифицированные парковки (по классам)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: ClassifiedParkingsFilters.jsx
 * Redux path: mapFilters.parkingClassLotStatuses
 * Style: CLASSIFIED_PARKING_STYLE.classItems (в layerStyles.js)
 */
export const CLASSIFIED_PARKING_FILTER = {
    id: 'classified-parking',
    title: defineMessage({ message: 'Классифицированные парковки' }),
    description: defineMessage({
        message: 'Классификация парковок по объёму стартов и финишей',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: CLASSIFIED_PARKING_STYLE,
    styleKey: 'classItems',

    reduxPath: 'parkingClassLotStatuses',
    defaultValue: [ 'top', 'middle' ],
    icon: 'circle',
};

// ============================================================================
// Категория 2: AREAS & ZONES - Фильтры территорий и зон
// ============================================================================

/**
 * FUNCTIONAL_AREAS_FILTER - Функциональные зоны
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: FunctionalAreasFilter.jsx
 * Redux path: mapFilters.functionalAreasStatuses
 * Style: FUNCTIONAL_AREAS_STYLE.zones (в layerStyles.js)
 */
export const FUNCTIONAL_AREAS_FILTER = {
    id: 'functional-areas',
    title: defineMessage({ message: 'Функциональные зоны' }),
    description: defineMessage({
        message: 'Зоны катания, парковки и запреты',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: FUNCTIONAL_AREAS_STYLE,
    styleKey: 'zones',

    reduxPath: 'functionalAreasStatuses',
    defaultValue: [ 'LIMITED', 'FORBIDDEN' ],
    icon: 'circle',
};

/**
 * RESTRICTION_ZONES_FILTER - Зоны ограничений
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: RestrictionZonesFilter.jsx
 * Redux path: mapFilters.restrictionZonesStatuses
 * Style: RESTRICTION_ZONES_STYLE.zones (в layerStyles.js)
 */
export const RESTRICTION_ZONES_FILTER = {
    id: 'restriction-zones',
    title: defineMessage({ message: 'Зоны ограничений' }),
    description: defineMessage({
        message: 'Зоны из agg_trips_august_mvt',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: RESTRICTION_ZONES_STYLE,
    styleKey: 'zones',

    reduxPath: 'restrictionZonesStatuses',
    defaultValue: [ 'LIMITED', 'FORBIDDEN', 'PERMITTED', 'STOP_LIMIT', 'SPEED_LIMIT' ], // все типы включены по умолчанию
    icon: 'circle',
};

/**
 * RESTRICTION_ZONES_V2_FILTER - Зоны ограничений
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: RestrictionZonesV2Filter.jsx
 * Redux path: mapFilters.restrictionZonesV2Statuses
 * Style: RESTRICTION_ZONES_STYLE.zones (в layerStyles.js)
 */
export const RESTRICTION_ZONES_V2_FILTER = {
    id: 'restriction-zones-v2',
    title: defineMessage({ message: 'Зоны ограничений' }),
    description: defineMessage({
        message: 'Зоны из martin.functional_areas_mvt',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: RESTRICTION_ZONES_V2_STYLE,
    styleKey: 'zones',

    reduxPath: 'restrictionZonesV2Statuses',
    defaultValue: [ 'LIMITED', 'FORBIDDEN', 'PERMITTED', 'STOP_LIMIT', 'SPEED_LIMIT' ], // все типы включены по умолчанию
    icon: 'circle',
};

/**
 * RESTRICTIONS_FILTER - Зоны ограничений конкурентов
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: RestrictionCompetitorsAreasFilter.jsx
 * Redux path: mapFilters.competitorsRestrictionZonesStatuses
 * Style: RESTRICTIONS_STYLE.zones (в layerStyles.js)
 */
export const RESTRICTIONS_FILTER = {
    id: 'restrictions',
    title: defineMessage({ message: 'Ограничения конкурентов' }),
    description: defineMessage({
        message: 'Зоны запрета парковки, катания и ограничения скорости',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: RESTRICTIONS_STYLE,
    styleKey: 'zones',

    reduxPath: 'competitorsRestrictionZonesStatuses',
    defaultValue: [ 'no_parking', 'no_entry', 'speed_limit', 'parking', 'other' ],
    icon: 'circle',
};

// ============================================================================
// Категория 3: AGGREGATED - Фильтры агрегированных данных
// ============================================================================

// ============================================================================
// Категория 1 ПРОДОЛЖЕНИЕ: PARKING - Дополнительные фильтры парковок
// ============================================================================

/**
 * PARKING_ALERTS_FILTER - Алерты парковок
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: ParkingFilters.jsx
 * Redux path: mapFilters.parkingsAlertsStatuses
 * Style: PARKING_ALERTS_STYLE.statuses (в layerStyles.js)
 */
export const PARKING_ALERTS_FILTER = {
    id: 'parking-alerts',
    title: defineMessage({ message: 'Алерты парковок' }),
    description: defineMessage({
        message: 'Разрешенные, ограниченные или запрещенные парковки',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: PARKING_ALERTS_STYLE,
    styleKey: 'statuses',

    reduxPath: 'parkingsAlertsStatuses',
    defaultValue: [ 'PERMITTED' ],
    icon: 'circle',
};

/**
 * PARKING_1300_FILTER - Статус парковок 1300
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: Parkings1300.jsx
 * Redux path: mapFilters.parkings1300Statuses
 * Style: PARKING_1300_STYLE.statuses (в layerStyles.js)
 */
export const PARKING_1300_FILTER = {
    id: 'parking-1300',
    title: defineMessage({ message: 'Парковки 1300' }),
    description: defineMessage({
        message: 'Статус соглашения по парковкам категории 1300',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: PARKING_1300_STYLE,
    styleKey: 'statuses',

    reduxPath: 'parkings1300Statuses',
    defaultValue: [ 'AGREED', 'PAID_FOR_PERIOD' ],
    icon: 'circle',
};

/**
 * PARKING_RADIUS_FILTER - Радиус парковок / Разрешение на парковку
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: ParkingRadiusFilter.jsx
 * Redux path: mapFilters.parkingRadiusStatuses
 * Items из variables.js: ParkingRadiusValues
 * Style: PARKING_PERMISSION_STYLE.statuses (в layerStyles.js)
 */
export const PARKING_RADIUS_FILTER = {
    id: 'parking-radius',
    title: defineMessage({ message: 'Радиус парковок' }),
    description: defineMessage({
        message: 'Фильтрация по разрешению и ограничениям',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: PARKING_PERMISSION_STYLE,
    styleKey: 'statuses',

    reduxPath: 'parkingRadiusStatuses',
    defaultValue: [ '1' ],
    icon: 'circle',
};

/**
 * SCR_TRACK_ROLES_FILTER — выключение отдельных ролей исполнителей (DA-14933)
 * Используется в: ScrTracksFilter.jsx; Redux path: mapFilters.scrTrackRoles
 * Style: SCR_TRACKS_STYLE.roles (ranger / service_car / charger / coordinator)
 */
export const SCR_TRACK_ROLES_FILTER = {
    id: 'scr-track-roles',
    title: defineMessage({ message: 'Роли исполнителей' }),
    description: defineMessage({ message: 'Показ треков по ролям' }),
    style: SCR_TRACKS_STYLE,
    styleKey: 'roles',
    reduxPath: 'scrTrackRoles',
    defaultValue: [ 'ranger', 'service_car', 'charger', 'coordinator' ],
    icon: 'line',
};

/**
 * SCR_INFRA_FILTER — включение/выключение точек инфраструктуры слоя треков (DA-14933)
 * Используется в: ScrTracksFilter.jsx; Redux path: mapFilters.scrInfraKinds
 * Style: SCR_INFRA_STYLE.kinds (parking / service_center / battery_cabinet / charge_station)
 */
export const SCR_INFRA_FILTER = {
    id: 'scr-infra',
    title: defineMessage({ message: 'Инфраструктура' }),
    description: defineMessage({ message: 'Показ точек инфраструктуры' }),
    style: SCR_INFRA_STYLE,
    styleKey: 'kinds',
    reduxPath: 'scrInfraKinds',
    defaultValue: [ 'parking', 'service_center', 'warehouse', 'battery_cabinet', 'charge_station' ],
    icon: 'circle',
};

/**
 * DOWNTIME_SCOOTERS_FILTER - Простой самокатов (в часах)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: ParkingDowntime48h.jsx
 * Redux path: mapFilters.downtimeScootersStatuses
 * Style: DOWNTIME_SCOOTERS_STYLE.durations (в layerStyles.js)
 */
export const DOWNTIME_SCOOTERS_FILTER = {
    id: 'downtime-scooters',
    title: defineMessage({ message: 'Простой самокатов' }),
    description: defineMessage({
        message: 'Фильтрация по времени простоя устройства (в часах)',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: DOWNTIME_SCOOTERS_STYLE,
    styleKey: 'durations',

    reduxPath: 'downtimeScootersStatuses',
    defaultValue: [ '0', '1', '2' ],
    icon: 'circle',
};

/**
 * CLASS_DAY_PART_FILTER - Части дня для классифицированных парковок
 * Используется в: ClassifiedParkingsDayPartFilters.jsx
 * Redux path: mapFilters.classifiedParkingDayPartStatuses
 * Items из variables.js: ClassParkingDayPartTypes
 */
export const CLASS_DAY_PART_FILTER = {
    id: 'class-day-part',
    title: defineMessage({ message: 'Части дня' }),
    description: defineMessage({
        message: 'Фильтрация классифицированных парковок по времени суток',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: CLASS_DAY_PART_STYLE,
    styleKey: 'parts',

    reduxPath: 'classifiedParkingDayPartStatuses',
    defaultValue: 1,
    icon: 'circle',
};

/**
 * CLASS_DAY_PART_FILTER_REBALANCED - Части дня для классифицированных парковок (ребаланс)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: ClassifiedParkingsDayPartRebalancedFilters.jsx
 * Redux path: mapFilters.classifiedParkingDayPartRebalancedStatuses
 * Items из variables.js: ClassParkingDayPartTypes
 * Style: CLASS_DAY_PART_STYLE.parts (в layerStyles.js, shared)
 */
export const CLASS_DAY_PART_FILTER_REBALANCED = {
    id: 'class-day-part-rebalanced',
    title: defineMessage({ message: 'Части дня (ребаланс)' }),
    description: defineMessage({
        message: 'Фильтрация классифицированных парковок по времени суток (ребаланс)',
    }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items (shared with CLASS_DAY_PART_FILTER)
    style: CLASS_DAY_PART_STYLE,
    styleKey: 'parts',

    reduxPath: 'classifiedParkingDayPartRebalancedStatuses',
    defaultValue: 1,
    icon: 'circle',
};

// ============================================================================
// Категория 2: AGGREGATED - Фильтры агрегированных данных (расширение)
// ============================================================================

/**
 * AGGREGATED_YEAR_FILTER - Год для фильтра агрегированных поездок
 * Используется в: TapsOpensHistFilter.jsx (multiple)
 * Redux path: mapFilters.tapsOpensHistYear
 * Items из variables.js: aggTracksFilterValues
 */
export const AGGREGATED_YEAR_FILTER = {
    id: 'aggregated-year',
    title: defineMessage({ message: 'Год (агрегированные поездки)' }),
    description: defineMessage({ message: 'Выбор года для анализа данных' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: AGGREGATED_YEAR_STYLE,
    styleKey: 'years',

    reduxPath: 'tapsOpensHistYear',
    defaultValue: '2024',
    icon: 'circle',
};

/**
 * AGG_STARTS_YEAR_FILTER - Год для стартов агрегированных поездок (август)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: AggStartTripsAugust.jsx
 * Redux path: mapFilters.aggStartsStatuses
 * Style: AGG_YEAR_AUGUST_STYLE.years (в layerStyles.js)
 */
export const AGG_STARTS_YEAR_FILTER = {
    id: 'agg-starts-year',
    title: defineMessage({ message: 'Год (старты в августе)' }),
    description: defineMessage({ message: 'Выбор года для анализа стартов' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: AGG_YEAR_AUGUST_STYLE,
    styleKey: 'years',

    reduxPath: 'aggStartsStatuses',
    defaultValue: '2024',
    icon: 'circle',
};

/**
 * AGG_FINISHES_YEAR_FILTER - Год для финишей агрегированных поездок (август)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: AggFinishTripsAugust.jsx
 * Redux path: mapFilters.aggFinishesStatuses
 * Style: AGG_YEAR_AUGUST_STYLE.years (в layerStyles.js)
 */
export const AGG_FINISHES_YEAR_FILTER = {
    id: 'agg-finishes-year',
    title: defineMessage({ message: 'Год (финиши в августе)' }),
    description: defineMessage({ message: 'Выбор года для анализа финишей' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: AGG_YEAR_AUGUST_STYLE,
    styleKey: 'years',

    reduxPath: 'aggFinishesStatuses',
    defaultValue: '2024',
    icon: 'circle',
};

/**
 * AGG_USER_TAPS_7D_FILTER - Легенда тапов за 7 дней (пользователь)
 * Используется в: TapsFilter.jsx
 * Redux path: Нет (только легенда)
 * Items из variables.js: aggUserTapsLegend7days
 */
export const AGG_USER_TAPS_7D_FILTER = {
    id: 'agg-user-taps-7d',
    title: defineMessage({ message: 'Тапы (7 дней)' }),
    description: defineMessage({ message: 'Легенда количества тапов пользователя за 7 дней' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: AGG_USER_TAPS_7D_STYLE,
    styleKey: 'ranges',

    reduxPath: 'aggUserTaps7dStatuses',
    defaultValue: [ '0', '1', '2', '3', '4', '5' ],
    icon: 'circle',
};

/**
 * AGG_USER_TAPS_30D_FILTER - Легенда тапов за 30 дней (пользователь)
 * Используется в: TapsFilter.jsx
 * Redux path: Нет (только легенда)
 * Items из variables.js: aggUserTapsLegend30days
 */
export const AGG_USER_TAPS_30D_FILTER = {
    id: 'agg-user-taps-30d',
    title: defineMessage({ message: 'Тапы (30 дней)' }),
    description: defineMessage({ message: 'Легенда количества тапов пользователя за 30 дней' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: AGG_USER_TAPS_30D_STYLE,
    styleKey: 'ranges',

    reduxPath: 'aggUserTaps30dStatuses',
    defaultValue: [ '0', '1', '2', '3', '4', '5' ],
    icon: 'circle',
};

/**
 * AGG_USER_OPENS_7D_FILTER - Легенда открытий за 7 дней (пользователь)
 * Используется в: AggUserOpensLegend.jsx
 * Redux path: Нет (только легенда)
 * Items из variables.js: aggUserOpensLegend7days
 */
export const AGG_USER_OPENS_7D_FILTER = {
    id: 'agg-user-opens-7d',
    title: defineMessage({ message: 'Открытия (7 дней)' }),
    description: defineMessage({ message: 'Легенда количества открытий пользователя за 7 дней' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: AGG_USER_OPENS_7D_STYLE,
    styleKey: 'ranges',

    reduxPath: 'aggUserOpens7dStatuses',
    defaultValue: [ '0', '1', '2', '3', '4', '5' ],
    icon: 'circle',
};

/**
 * AGG_USER_OPENS_30D_FILTER - Легенда открытий за 30 дней (пользователь)
 * Используется в: AggUserOpensLegend.jsx
 * Redux path: Нет (только легенда)
 * Items из variables.js: aggUserOpensLegend30days
 */
export const AGG_USER_OPENS_30D_FILTER = {
    id: 'agg-user-opens-30d',
    title: defineMessage({ message: 'Открытия (30 дней)' }),
    description: defineMessage({ message: 'Легенда количества открытий пользователя за 30 дней' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: AGG_USER_OPENS_30D_STYLE,
    styleKey: 'ranges',

    reduxPath: 'aggUserOpens30dStatuses',
    defaultValue: [ '0', '1', '2', '3', '4', '5' ],
    icon: 'circle',
};

// ============================================================================
// КАРТА ВСЕХ ФИЛЬТРОВ (для быстрого доступа)
// ============================================================================

// ВАЖНО: FILTER_CONFIGS определяется в конце файла (после всех фильтров)

// ============================================================================
// КАТЕГОРИЯ: TRIPS (Поездки)
// ============================================================================

/**
 * TAPS_DAYS_FILTER - Количество дней для агрегации тапов
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: TapsFilter.jsx
 * Redux path: mapFilters.tapsDays
 * Style: TAPS_OPENS_DAYS_STYLE.days (в layerStyles.js)
 */
export const TAPS_DAYS_FILTER = {
    id: 'taps-days',
    title: defineMessage({ message: 'Дни тапов' }),
    description: defineMessage({ message: 'Количество дней для агрегации тапов пользователей' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: TAPS_OPENS_DAYS_STYLE,
    styleKey: 'days',

    reduxPath: 'tapsDays',
    defaultValue: 7,
    icon: 'circle',
};

/**
 * OPENS_DAYS_FILTER - Количество дней для агрегации открытий
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: TapsFilter.jsx (OpensFilter)
 * Redux path: mapFilters.opensDays
 * Style: TAPS_OPENS_DAYS_STYLE.days (в layerStyles.js)
 */
export const OPENS_DAYS_FILTER = {
    id: 'opens-days',
    title: defineMessage({ message: 'Дни открытий' }),
    description: defineMessage({ message: 'Количество дней для агрегации открытий приложения' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: TAPS_OPENS_DAYS_STYLE,
    styleKey: 'days',

    reduxPath: 'opensDays',
    defaultValue: 7,
    icon: 'circle',
};

/**
 * TAPS_MODE_FILTER - Режим отображения тапов (абсолютное значение или нормализованное)
 * Используется в: TapsFilter.jsx
 * Redux path: mapFilters.tapsMode
 * Items: ABS или SHARE (нормализованное)
 */
export const TAPS_MODE_FILTER = {
    id: 'taps-mode',
    title: defineMessage({ message: 'Режим тапов' }),
    description: defineMessage({ message: 'Абсолютное значение или нормализованное (в % от среднего)' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: TAPS_OPENS_MODE_STYLE,
    styleKey: 'modes',

    reduxPath: 'tapsMode',
    defaultValue: V.ABS,
    icon: 'circle',
};

/**
 * OPENS_MODE_FILTER - Режим отображения открытий (абсолютное значение или нормализованное)
 * Используется в: TapsFilter.jsx (OpensFilter)
 * Redux path: mapFilters.opensMode
 * Items: ABS или SHARE (нормализованное)
 */
export const OPENS_MODE_FILTER = {
    id: 'opens-mode',
    title: defineMessage({ message: 'Режим открытий' }),
    description: defineMessage({ message: 'Абсолютное значение или нормализованное (в % от среднего)' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: TAPS_OPENS_MODE_STYLE,
    styleKey: 'modes',

    reduxPath: 'opensMode',
    defaultValue: V.ABS,
    icon: 'circle',
};

/**
 * TAPS_NO_STARTS_DAYS_FILTER - Количество дней для тапов без стартов
 * Используется в: TapsFilter.jsx
 * Redux path: mapFilters.tapsNoStartsDays
 * Items: 7 или 30 дней
 */
export const TAPS_NO_STARTS_DAYS_FILTER = {
    id: 'taps-no-starts-days',
    title: defineMessage({ message: 'Дни тапов без стартов' }),
    description: defineMessage({ message: 'Количество дней для агрегации тапов без соответствующих стартов' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: TAPS_OPENS_PERIODS_STYLE,
    styleKey: 'periods',

    reduxPath: 'tapsNoStartsDays',
    defaultValue: 7,
    icon: 'circle',
};

/**
 * OPENS_TO_STARTS_DAYS_FILTER - Количество дней для открытий до стартов
 * Используется в: AggUserOpensToStartsLegend.jsx
 * Redux path: mapFilters.OpensToStartDays
 * Items: 7 или 30 дней
 */
export const OPENS_TO_STARTS_DAYS_FILTER = {
    id: 'opens-to-starts-days',
    title: defineMessage({ message: 'Дни открытий до стартов' }),
    description: defineMessage({ message: 'Количество дней для агрегации открытий до стартов' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: TAPS_OPENS_PERIODS_STYLE,
    styleKey: 'periods',

    reduxPath: 'OpensToStartDays',
    defaultValue: 7,
    icon: 'circle',
};

/**
 * START_TRACK_DAYS_FILTER - Количество дней для фильтра старт треков
 * Используется в: StartFinishTrackFilter.jsx
 * Redux path: mapFilters.startTrackDays
 * Items: 1, 7, 30 дней
 */
export const START_TRACK_DAYS_FILTER = {
    id: 'start-track-days',
    title: defineMessage({ message: 'Дни старт треков' }),
    description: defineMessage({ message: 'Количество дней для фильтра агрегированных старт треков' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: START_FINISH_TRACK_DAYS_STYLE,
    styleKey: 'periods',

    reduxPath: 'startTrackDays',
    defaultValue: 7,
    icon: 'circle',
};

/**
 * START_TRACK_MODE_FILTER - Режим отображения старт треков (абсолютное или нормализованное)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: StartFinishTrackFilter.jsx
 * Redux path: mapFilters.startTrackMode
 * Style: TRACK_MODE_STYLE.modes (в layerStyles.js)
 */
export const START_TRACK_MODE_FILTER = {
    id: 'start-track-mode',
    title: defineMessage({ message: 'Режим старт треков' }),
    description: defineMessage({ message: 'Абсолютное значение или нормализованное' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: TRACK_MODE_STYLE,
    styleKey: 'modes',

    reduxPath: 'startTrackMode',
    defaultValue: V.ABS,
    icon: 'circle',
};

/**
 * FINISH_TRACK_DAYS_FILTER - Количество дней для фильтра финиш треков
 * Используется в: StartFinishTrackFilter.jsx
 * Redux path: mapFilters.finishTrackDays
 * Items: 1, 7, 30 дней
 */
export const FINISH_TRACK_DAYS_FILTER = {
    id: 'finish-track-days',
    title: defineMessage({ message: 'Дни финиш треков' }),
    description: defineMessage({ message: 'Количество дней для фильтра агрегированных финиш треков' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: START_FINISH_TRACK_DAYS_STYLE,
    styleKey: 'periods',

    reduxPath: 'finishTrackDays',
    defaultValue: 7,
    icon: 'circle',
};

/**
 * FINISH_TRACK_MODE_FILTER - Режим отображения финиш треков (абсолютное или нормализованное)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: StartFinishTrackFilter.jsx
 * Redux path: mapFilters.finishTrackMode
 * Style: TRACK_MODE_STYLE.modes (в layerStyles.js)
 */
export const FINISH_TRACK_MODE_FILTER = {
    id: 'finish-track-mode',
    title: defineMessage({ message: 'Режим финиш треков' }),
    description: defineMessage({ message: 'Абсолютное значение или нормализованное' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: TRACK_MODE_STYLE,
    styleKey: 'modes',

    reduxPath: 'finishTrackMode',
    defaultValue: V.ABS,
    icon: 'circle',
};

/**
 * TRACK_DAYS_FILTER - Период для агрегированных поездок (неделя/выходные/будни)
 * Используется в: TrackFilter.jsx
 * Redux path: mapFilters.TrackDays
 * Items: неделя, будни, выходные
 */
export const TRACK_DAYS_FILTER = {
    id: 'track-days',
    title: defineMessage({ message: 'Период агрегированных поездок' }),
    description: defineMessage({ message: 'Фильтр по периодам: неделя, будни или выходные' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items с V.map
    style: TRACK_DAYS_STYLE,
    styleKey: 'days',

    reduxPath: 'TrackDays',
    defaultValue: 'week',
    icon: 'circle',
};

/**
 * TRACK_PREFIX_FILTER - Тип агрегированных поездок (все/самокаты/велосипеды)
 * Используется в: TrackFilter.jsx
 * Redux path: mapFilters.TrackPrefix
 * Items: все поездки, самокаты, велосипеды, самокаты тандем
 */
export const TRACK_PREFIX_FILTER = {
    id: 'track-prefix',
    title: defineMessage({ message: 'Тип агрегированных поездок' }),
    description: defineMessage({ message: 'Фильтр по типам транспорта' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items с V.map
    style: TRACK_PREFIX_STYLE,
    styleKey: 'types',

    reduxPath: 'TrackPrefix',
    defaultValue: 'all',
    icon: 'circle',
};

// ============================================================================
// КАТЕГОРИЯ: AREAS (Зоны и территории)
// ============================================================================

/**
 * FUNCTIONAL_AREAS_YANDEX_URENT_FILTER - Функциональные зоны Yandex/Urent
 * Используется в: FunctionalAreasYandexUrentFilter.jsx
 * Redux path: mapFilters.functionalAreasYandexUrentStatuses
 * Items: Yandex, Urent, Jet, Lime (с цветами)
 */
export const FUNCTIONAL_AREAS_YANDEX_URENT_FILTER = {
    id: 'functional-areas-yandex-urent',
    title: defineMessage({ message: 'Функциональные зоны конкурентов' }),
    description: defineMessage({ message: 'Зоны Yandex, Urent, Jet, Lime' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: FUNCTIONAL_AREAS_YANDEX_URENT_STYLE,
    styleKey: 'zones',

    reduxPath: 'functionalAreasYandexUrentStatuses',
    defaultValue: [],
    icon: 'circle',
};

/**
 * LISBOA_ZONES_FILTER - Зоны ограничений в Лиссабоне
 * Используется в: LisboaZonesFilter.jsx
 * Redux path: mapFilters.LisboaZonesStatuses
 * Items: No parking, Forbidden, Mandatory parking
 */
export const LISBOA_ZONES_FILTER = {
    id: 'lisboa-zones',
    title: defineMessage({ message: 'Зоны ограничений в Лиссабоне' }),
    description: defineMessage({ message: 'Зоны запрета/обязательной парковки' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: LISBOA_ZONES_STYLE,
    styleKey: 'zones',

    reduxPath: 'LisboaZonesStatuses',
    defaultValue: [ 'Forbidden' ],
    icon: 'circle',
};

/**
 * ANALYTIC_AREAS_FILTER - Аналитические зоны (транспортный каркас / бизнес)
 * Используется в: AnalyticAreasFilter.jsx
 * Redux path: mapFilters.AnalyticArea
 * Items: 0 (Транспортный каркас), 1 (Бизнес)
 */
export const ANALYTIC_AREAS_FILTER = {
    id: 'analytic-areas',
    title: defineMessage({ message: 'Аналитические зоны' }),
    description: defineMessage({ message: 'Тип аналитической зоны' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: ANALYTIC_AREAS_STYLE,
    styleKey: 'zones',

    reduxPath: 'AnalyticArea',
    defaultValue: 1,
    icon: 'circle',
};

/**
 * ISOCHRONES_WITH_SCOOTERS_FILTER - Изохроны доступности парковок
 * Используется в: IsochronesWithScootersFilters.jsx
 * Redux path: mapFilters.isochronesWithScootersFilterStatuses
 * Items: Все изохроны / Изохроны с самокатами
 */
export const ISOCHRONES_WITH_SCOOTERS_FILTER = {
    id: 'isochrones-with-scooters',
    title: defineMessage({ message: 'Изохроны парковок' }),
    description: defineMessage({ message: 'Показать все или только с самокатами' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: ISOCHRONES_WITH_SCOOTERS_STYLE,
    styleKey: 'types',

    reduxPath: 'isochronesWithScootersFilterStatuses',
    defaultValue: [ '0' ],
    icon: 'circle',
};

/**
 * OPERATION_AREAS_FILTER - Операционные зоны
 * Используется в: OperationAreasFilter.jsx
 * Redux path: mapFilters.operationAreasStatuses (если используется)
 * Items: Зоны эксплуатации
 * Статус: Используется в компонентах (требует уточнения структуры)
 */
export const OPERATION_AREAS_FILTER = {
    id: 'operation-areas',
    title: defineMessage({ message: 'Операционные зоны' }),
    description: defineMessage({ message: 'Фильтр по операционным зонам' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items
    style: OPERATION_AREAS_STATUS_STYLE,
    styleKey: 'statuses',

    reduxPath: 'operationAreasStatuses',
    defaultValue: [ 'active' ],
    icon: 'circle',
};

/**
 * OPERATION_AREAS_ROTATION_FILTER - Периоды ротации операционных зон
 * Используется в: OperationAreasRotation2Filter.jsx
 * Redux path: mapFilters.oaRotationDays
 * Items: 1, 7, 14, 30 дней
 */
export const OPERATION_AREAS_ROTATION_FILTER = {
    id: 'operation-areas-rotation',
    title: defineMessage({ message: 'Период ротации зон' }),
    description: defineMessage({ message: 'Количество дней для анализа ротации' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map (переиспользуем OA_ROTATION_DAYS_STYLE)
    style: OA_ROTATION_DAYS_STYLE,
    styleKey: 'periods',

    reduxPath: 'oaRotationDays',
    defaultValue: 7,
    icon: 'circle',
};

// ============================================================================
// КАТЕГОРИЯ: SERVICE & DEVICES (Сервисное)
// ============================================================================

/**
 * SERVICE_CENTERS_FILTER - Типы сервисных центров
 * Используется в: ServiceCentersFilters.jsx
 * Redux path: mapFilters.ServiceCentersStatuses
 * Items: ACTIVE, СЦ, СЗ, Шкаф, PROSPECTIVE, ПСЦ-АДМИН, ПСЗ-АДМИН, ПШкаф
 */
export const SERVICE_CENTERS_FILTER = {
    id: 'service-centers',
    title: defineMessage({ message: 'Сервисные центры' }),
    description: defineMessage({ message: 'Типы инфраструктуры сервисных центров' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: SERVICE_CENTERS_FILTER_STYLE,
    styleKey: 'centers',

    reduxPath: 'ServiceCentersStatuses',
    defaultValue: [ 'ACTIVE' ],
    icon: 'circle',
};

/**
 * POI_FILTER - Точки интереса (POI)
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: PoiLegend.jsx
 * Redux path: mapFilters.poiStatuses
 * Стиль: POI_STYLE.categories -> filterItems
 */
export const POI_FILTER = {
    id: 'poi',
    title: defineMessage({ message: 'Точки интереса' }),
    description: defineMessage({ message: 'Категории POI для отображения на карте' }),
    style: POI_STYLE,
    styleKey: 'categories',
    reduxPath: 'poiStatuses',
    defaultValue: V.poiLegendValues,
    icon: 'circle',
};

/**
 * ALERTS_TYPES_FILTER - Типы алертов парковок
 * Используется в: AlertsTypesFilter.jsx
 * Redux path: mapFilters.AlertsTypesStatuses
 * Items: DENIED, LIMITED, PERMITTED
 */
export const ALERTS_TYPES_FILTER = {
    id: 'alerts-types',
    title: defineMessage({ message: 'Типы алертов' }),
    description: defineMessage({ message: 'Фильтр по типам алертов парковок' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: ALERTS_TYPES_STYLE,
    styleKey: 'types',

    reduxPath: 'AlertsTypesStatuses',
    defaultValue: 1,
    icon: 'circle',
};

/**
 * ALERTS_DEVICE_TYPES_FILTER - Типы устройств для алертов
 * Используется в: AlertsDeviceTypeFilter.jsx
 * Redux path: mapFilters.AlertsDeviceStatuses
 * Items: DEVICE, PHONE
 */
export const ALERTS_DEVICE_TYPES_FILTER = {
    id: 'alerts-device-types',
    title: defineMessage({ message: 'Типы устройств алертов' }),
    description: defineMessage({ message: 'Фильтр по типам устройств (DEVICE/PHONE)' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: ALERTS_DEVICE_TYPES_STYLE,
    styleKey: 'devices',

    reduxPath: 'AlertsDeviceStatuses',
    defaultValue: 1,
    icon: 'circle',
};

/**
 * SPEED_LIMITS_FILTER - Ограничения скорости
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: SpeedLimitsFilter.jsx
 * Redux path: mapFilters.speedLimitsStatuses
 * Style: SPEED_LIMITS_STYLE.limits (в layerStyles.js)
 */
export const SPEED_LIMITS_FILTER = {
    id: 'speed-limits',
    title: defineMessage({ message: 'Ограничения скорости' }),
    description: defineMessage({ message: 'Типы ограничений скорости и парковки' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо items
    style: SPEED_LIMITS_STYLE,
    styleKey: 'limits',

    reduxPath: 'speedLimitsStatuses',
    defaultValue: [ 'STOP_LIMIT', 'SPEED_LIMIT', 'STOP_LIMIT_with_pause' ],
    icon: 'circle',
};

/**
 * PARKINGS_ALERTS_POINTS_DEVICE_FILTER - Типы устройств для точек алертов парковок
 * Используется в: ParkingsAlertsPointsDeviceFilter.jsx
 * Redux path: mapFilters.ParkingsAlertsPointsDeviceStatuses
 * Items: PHONE, DEVICE (с цветами)
 */
export const PARKINGS_ALERTS_POINTS_DEVICE_FILTER = {
    id: 'parkings-alerts-points-device',
    title: defineMessage({ message: 'Устройства алертов парковок' }),
    description: defineMessage({ message: 'Фильтр по типам устройств' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: PARKINGS_ALERTS_DEVICE_STYLE,
    styleKey: 'devices',

    reduxPath: 'ParkingsAlertsPointsDeviceStatuses',
    defaultValue: [ 'PHONE', 'DEVICE' ],
    icon: 'circle',
};

/**
 * PARKINGS_ALERTS_POINTS_PERIOD_FILTER - Периоды для точек алертов парковок
 * Используется в: ParkingsAlertsPointsFilter.jsx
 * Redux path: mapFilters.parkingsAlertsPointsPeriodStatuses
 * Items: 1, 3 (дни)
 */
export const PARKINGS_ALERTS_POINTS_PERIOD_FILTER = {
    id: 'parkings-alerts-points-period',
    title: defineMessage({ message: 'Период алертов парковок' }),
    description: defineMessage({ message: 'Период анализа для алертов' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: PARKINGS_ALERTS_PERIOD_STYLE,
    styleKey: 'periods',

    reduxPath: 'parkingsAlertsPointsPeriodStatuses',
    defaultValue: 3,
    icon: 'circle',
};

/**
 * PARKINGS_ALERTS_POINTS_TYPE_ERROR_FILTER - Типы ошибок алертов парковок
 * Используется в: ParkingsAlertsPointsTypeErrorFilter.jsx
 * Redux path: mapFilters.parkingsAlertsPointsTypeErrorStatuses
 * Items: 11 типов ошибок с кодами и цветами + All cases
 */
export const PARKINGS_ALERTS_POINTS_TYPE_ERROR_FILTER = {
    id: 'parkings-alerts-points-type-error',
    title: defineMessage({ message: 'Типы ошибок алертов' }),
    description: defineMessage({ message: 'Фильтр по типам ошибок парковок' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: PARKINGS_ALERTS_ERROR_TYPES_STYLE,
    styleKey: 'errors',

    reduxPath: 'parkingsAlertsPointsTypeErrorStatuses',
    defaultValue: 12,
    icon: 'circle',
};

// ============================================================================
// КАТЕГОРИЯ: TIME & PERIOD (Время и периоды)
// ============================================================================

/**
 * PERIOD_FILTER - Период для фильтров (неделя/выходные/будни)
 * Используется в: CorrespondingMatrixFilters.jsx
 * Redux path: mapFilters.correspondingMatrixPeriod
 * Items: week, weekdays, weekends
 */
export const PERIOD_FILTER = {
    id: 'period',
    title: defineMessage({ message: 'Период' }),
    description: defineMessage({ message: 'Выбор периода: неделя, будни или выходные' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо hardcoded items (shared with TRACK_DAYS_FILTER)
    style: TRACK_DAYS_STYLE,
    styleKey: 'days',

    reduxPath: 'correspondingMatrixPeriod',
    defaultValue: 'week',
    icon: 'circle',
};

/**
 * COMPETITORS_PREFIX_FILTER - Режим отображения конкурентов
 * Используется в: StartCompetitorsFilter.jsx
 * Redux path: mapFilters.CompetitorsPrefix
 * Items: starts (ABS), ms (MARKET SHARE), leadership (LEADER)
 */
export const COMPETITORS_PREFIX_FILTER = {
    id: 'competitors-prefix',
    title: defineMessage({ message: 'Режим отображения конкурентов' }),
    description: defineMessage({ message: 'Абсолютные значения, доля рынка или лидеры' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: COMPETITORS_PREFIX_MODE_STYLE,
    styleKey: 'modes',

    reduxPath: 'CompetitorsPrefix',
    defaultValue: 'leadership',
    icon: 'circle',
};

/**
 * COMPETITORS_DAYS_FILTER - Период для анализа конкурентов
 * ✅ ИСПОЛЬЗУЕТ FILTER TEMPLATES - данные из layerStyles.js
 *
 * Используется в: StartCompetitorsFilter.jsx
 * Redux path: mapFilters.CompetitorsDays
 * Items: 1, 7, 30 дней
 * Style: COMPETITORS_DAYS_STYLE.periods (в layerStyles.js)
 */
export const COMPETITORS_DAYS_FILTER = {
    id: 'competitors-days',
    title: defineMessage({ message: 'Период анализа конкурентов' }),
    description: defineMessage({ message: 'Количество дней для анализа' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: COMPETITORS_DAYS_STYLE,
    styleKey: 'periods',

    reduxPath: 'CompetitorsDays',
    defaultValue: 7,
    icon: 'circle',
};

/**
 * COMPETITOR_FILTER - Выбор конкурента
 * Используется в: StartCompetitorsFilter.jsx
 * Redux path: mapFilters.Competitor
 * Items: whoosh, yandex, urent, jet, eleven, lime, lite
 */
export const COMPETITOR_FILTER = {
    id: 'competitor',
    title: defineMessage({ message: 'Выбор конкурента' }),
    description: defineMessage({ message: 'Сервис для сравнения' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: COMPETITORS_FILTER_STYLE,
    styleKey: 'competitors',

    reduxPath: 'Competitor',
    defaultValue: 'whoosh',
    icon: 'circle',
};

/**
 * SB_WITH_ERRORS_FILTER - Типы ошибок SB (Service Bikes)
 * Используется в: SbWithErrorsFilter.jsx
 * Redux path: mapFilters.sbWithErrorsStatuses
 * Items: Error code 21 (Contact with battery), Error code 50 (Battery type)
 */
export const SB_WITH_ERRORS_FILTER = {
    id: 'sb-with-errors',
    title: defineMessage({ message: 'Ошибки сервисных самокатов' }),
    description: defineMessage({ message: 'Типы ошибок батареи и оборудования' }),
    // ✅ НОВАЯ АРХИТЕКТУРА: ссылка на стиль вместо V.map
    style: SB_WITH_ERRORS_STYLE,
    styleKey: 'errors',

    reduxPath: 'sbWithErrorsStatuses',
    defaultValue: V.SbWithErrorsValues,
    icon: 'circle',
};

/**
 * Хелпер для получения начальных значений всех фильтров
 * Использование: const initialState = getFiltersInitialState();
 * Результат: { parkingLotStatuses: ['VERIFIED'], parkingClassLotStatuses: ['top', 'middle'], ... }
 */

// ============================================================================
// КАРТА ВСЕХ ФИЛЬТРОВ (определяется в конце после всех определений)
// ============================================================================

export const FILTER_CONFIGS = {
    PARKING_FILTER,
    PARKING_ALERTS_FILTER,
    PARKING_1300_FILTER,
    PARKING_RADIUS_FILTER,
    SCR_TRACK_ROLES_FILTER,
    SCR_INFRA_FILTER,
    DOWNTIME_SCOOTERS_FILTER,
    CLASS_DAY_PART_FILTER,
    CLASS_DAY_PART_FILTER_REBALANCED,
    CLASSIFIED_PARKING_FILTER,
    FUNCTIONAL_AREAS_FILTER,
    RESTRICTION_ZONES_FILTER,
    RESTRICTION_ZONES_V2_FILTER,
    RESTRICTIONS_FILTER,
    AGGREGATED_YEAR_FILTER,
    AGG_STARTS_YEAR_FILTER,
    AGG_FINISHES_YEAR_FILTER,
    AGG_USER_TAPS_7D_FILTER,
    AGG_USER_TAPS_30D_FILTER,
    AGG_USER_OPENS_7D_FILTER,
    AGG_USER_OPENS_30D_FILTER,
    TAPS_DAYS_FILTER,
    OPENS_DAYS_FILTER,
    TAPS_MODE_FILTER,
    OPENS_MODE_FILTER,
    TAPS_NO_STARTS_DAYS_FILTER,
    OPENS_TO_STARTS_DAYS_FILTER,
    START_TRACK_DAYS_FILTER,
    START_TRACK_MODE_FILTER,
    FINISH_TRACK_DAYS_FILTER,
    FINISH_TRACK_MODE_FILTER,
    TRACK_DAYS_FILTER,
    TRACK_PREFIX_FILTER,
    FUNCTIONAL_AREAS_YANDEX_URENT_FILTER,
    LISBOA_ZONES_FILTER,
    ANALYTIC_AREAS_FILTER,
    ISOCHRONES_WITH_SCOOTERS_FILTER,
    OPERATION_AREAS_FILTER,
    OPERATION_AREAS_ROTATION_FILTER,
    SERVICE_CENTERS_FILTER,
    POI_FILTER,
    ALERTS_TYPES_FILTER,
    ALERTS_DEVICE_TYPES_FILTER,
    SPEED_LIMITS_FILTER,
    PARKINGS_ALERTS_POINTS_DEVICE_FILTER,
    PARKINGS_ALERTS_POINTS_PERIOD_FILTER,
    PARKINGS_ALERTS_POINTS_TYPE_ERROR_FILTER,
    PERIOD_FILTER,
    COMPETITORS_PREFIX_FILTER,
    COMPETITORS_DAYS_FILTER,
    COMPETITOR_FILTER,
    SB_WITH_ERRORS_FILTER,
};

/**
 * Хелпер для получения фильтра по ID
 * Использование: getFilterById('parking')
 */
export const getFilterById = filterId => {
    const filter = Object.values(FILTER_CONFIGS).find(f => f.id === filterId);
    if (!filter) {
        console.warn(`Filter with id "${filterId}" not found`);
    }
    return filter;
};

export const getFiltersInitialState = () => {
    const state = {};
    Object.values(FILTER_CONFIGS).forEach(filter => {
        state[filter.reduxPath] = filter.defaultValue;
    });
    return state;
};

// ============================================================================
// ПЛАН РАСШИРЕНИЯ (для фаз 2+)
// ============================================================================

/*
Следующие фильтры планируются для добавления:

КАТЕГОРИЯ: PARKING (Парковки) - Расширение
- PARKING_ALERTS_FILTER (parkingsAlerts)
- PARKING_1300_FILTER (parkings1300)
- PARKING_RADIUS_FILTER (ParkingRadiusValues)
- DOWNTIME_SCOOTERS_FILTER (downtimeScootersLegend)

КАТЕГОРИЯ: TRIPS (Поездки)
- TAPS_DAYS_FILTER
- OPENS_DAYS_FILTER
- TAPS_MODE_FILTER (ABS / REL)
- STARTS_BY_HOUR_FILTER
- TRACK_MODE_FILTER

КАТЕГОРИЯ: AGGREGATED (Расширение)
- AGGREGATED_TRACK_DAYS_FILTER (aggregatedTracksDayFilterValues)
- AGGREGATED_YEAR_FILTER (aggTracksAugustStatusesYear)
- AGGREGATED_STARTS_FILTER (aggStartsStatuses)
- AGGREGATED_FINISHES_FILTER (aggFinishesStatuses)

КАТЕГОРИЯ: SERVICE & DEVICES
- SERVICE_CENTERS_FILTER (ServiceCentersStatuses)
- POI_FILTER (poiLegendValues)
- ALERTS_TYPES_FILTER (AlertsTypesFilterValues)
- ALERTS_DEVICE_FILTER (AlertsDeviceTypesFilterValues)

КАТЕГОРИЯ: TIME & PERIOD
- PERIOD_FILTER (correspondingMatrixPeriod)
- YEAR_SELECT_FILTER (tapsOpensHistYear)

И многие другие... (всего ~50 фильтров)
*/
